import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Task } from '../entity/task';
import { toTaskTypeTransformer } from '../state/transformers/transformers';
import { TaskType } from '../state/types';
import { BLService } from './bl.service';

@Injectable({ providedIn: 'root' })
export class TaskService extends BLService {
  private RESOURCE_ENDPOINT = 'task';

  getAllTasks(): Observable<Task[]> {
    return this.httpGet<Task[]>(this.RESOURCE_ENDPOINT);
  }

  getAllTaskTypes(): Observable<TaskType[]> {
    return this.getAllTasks().pipe(map((tasks) => tasks.map(toTaskTypeTransformer.transform)));
  }

  getTask(id: number): Observable<Task> {
    return this.httpGet<Task>(`${this.RESOURCE_ENDPOINT}/${id}`);
  }

  getAgentUrl(id: number, date: string): Observable<{ agentCall: string }> {
    return this.httpGet<{ agentCall: string }>(`${this.RESOURCE_ENDPOINT}/agent-call/${id}/${date}`);
  }

  getTasksTemplates(taskGroupId = 0): Observable<Task[]> {
    const url = `${this.RESOURCE_ENDPOINT}/templates/${taskGroupId}`;
    return this.httpGet<Task[]>(url);
  }

  getTaskTemplateTypes(taskGroupId = 0): Observable<TaskType[]> {
    return this.getTasksTemplates(taskGroupId).pipe(map((tasks) => tasks.map(toTaskTypeTransformer.transform)));
  }

  getActiveTaskTypeTemplates(): Observable<TaskType[]> {
    return this.getTaskTemplateTypes().pipe(map((templates) => templates.filter((t) => !t.archived)));
  }

  getTasksByClientId(clientId: number): Observable<Task[]> {
    return this.httpGet<Task[]>(`${this.RESOURCE_ENDPOINT}/by-client/${clientId}`);
  }

  getTaskTypesByClientId(clientId: number): Observable<TaskType[]> {
    return this.getTasksByClientId(clientId).pipe(map((tasks) => tasks.map((t) => toTaskTypeTransformer.transform(t))));
  }

  getAllTasksByCode(taskCode: string): Observable<Task[]> {
    return this.httpGet<Task[]>(`${this.RESOURCE_ENDPOINT}/by-code/${taskCode}`);
  }

  saveTaskTemplate(task: TaskType): Observable<TaskType> {
    const body = JSON.stringify(task);

    return this.httpPut<TaskType>(`${this.RESOURCE_ENDPOINT}/template`, body);
  }

  saveTask(task: Task): Observable<Task[]> {
    const body = JSON.stringify(task);
    return this.httpPut<Task[]>(`${this.RESOURCE_ENDPOINT}/client/${task.client.id}`, body);
  }

  saveTasks(tasks: Task[]): Observable<Task[]> {
    const body = JSON.stringify(tasks);
    return this.httpPut<Task[]>(`${this.RESOURCE_ENDPOINT}/client/${tasks[0].client.id}`, body);
  }

  deleteTask(id: number): Observable<Task[]> {
    return this.httpDelete<Task[]>(`${this.RESOURCE_ENDPOINT}/${id}`);
  }

  getAllDeletableTasks(): Observable<Task[]> {
    return this.httpGet<Task[]>(`${this.RESOURCE_ENDPOINT}/deletable`);
  }
}
